import { Bookmark, Rate } from '@/logic/services';
import { ElementType, forwardRef } from 'react';
import { Stack, SxProps, Theme, Typography } from '@mui/material';

import { BookmarkName } from './bookmark-name';
import ImageBoxFuture from './image-box-future';
import { pxToRem } from '@/theme/typography';

const MovieFallbackImg = '/icons/my-booking/old-user-booking-card-empty-icon.svg';

export interface MovieCardProps {
  title: string;
  language: string;
  genres: string[];
  img: string;
  bookmark?: Bookmark;
  rate?: Rate;
  isAdvancedBooking: boolean;
  priority?: boolean;
  /**
   * `sizes` for the poster. Defaults to the movies grid (movies-list.tsx): 2
   * columns on phones, 3 from 500 px, 4 from the laptop breakpoint.
   */
  sizes?: string;
  sx?: SxProps;
  component?: ElementType;
  componentProps?: {
    disabled?: boolean;
    onClick?: () => void;
  };
}

export type Ref = HTMLButtonElement | HTMLDivElement;

// Every sx literal that used to be inlined below was rebuilt on each render of
// each card — ~7 fresh objects per card, serialized again by Emotion every
// time, multiplied by every card in the grid/carousel. Hoisting them to module
// constants (theme-callback form for the theme-dependent ones) keeps the
// object identities stable so that per-render cost is paid once, not
// cards × renders times. Measured as the top main-thread cost on the mobile
// Lighthouse run; only the bookmark/rate blocks stay dynamic because their
// colors come from API data.
const genreDotSx = (theme: Theme) =>
  ({
    color: theme.palette.layout.posterDotColor,
    marginX: pxToRem(4),
  } as const);

const cardSx = (theme: Theme) =>
  ({
    position: 'relative',
    height: '100%',
    backgroundColor: theme.palette.layout.bgPosterCard,
    boxShadow: theme.customShadows.cardShadow,
    borderRadius: '20px',
    textDecoration: 'none',
    transitionDuration: '200ms',
    transitionProperty: 'transform',
    ':hover': {
      cursor: 'pointer',
      transform: {
        tablet: 'scale(1.05)',
      },
    },
  } as const);

const imageSx = {
  borderRadius: '20px',
  overflow: 'hidden',
} as const;

const infoStackSx = {
  paddingX: pxToRem(16),
  paddingBottom: pxToRem(16),
  flexGrow: 1,
  textAlign: 'left',
} as const;

const titleSx = (theme: Theme) =>
  ({
    marginTop: pxToRem(16),
    marginBottom: {
      mobile: '28px',
      mediumDesktop: '7px',
    },
    fontSize: pxToRem(20),
    fontFamily: 'co-head-line-corp-bold',
    color: theme.palette.common.black,
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitLineClamp: '3',
    WebkitBoxOrient: 'vertical',
  } as const);

const languageSx = (theme: Theme) =>
  ({
    display: 'block',
    marginTop: 'auto',
    color: theme.palette.layout.posterCardInfoColor,
  } as const);

const genresRowSx = (theme: Theme) =>
  ({
    marginTop: pxToRem(6),
    color: theme.palette.layout.posterCardInfoColor,
    alignItems: 'center',
  } as const);

const bookmarkBaseSx = {
  fontSize: {
    mobile: '9px',
    mediumDesktop: '11px',
  },
  position: 'absolute',
  top: pxToRem(16),
  left: pxToRem(0),
  paddingX: {
    mobile: '6px',
    mediumDesktop: '12px',
  },
  paddingY: {
    mobile: '2.5px',
    mediumDesktop: '7px',
  },
  borderTopRightRadius: '20px',
  borderBottomRightRadius: '20px',
} as const;

const rateBaseSx = {
  fontSize: {
    mobile: '9px',
    mediumDesktop: '12px',
    extraLargeDesktop: '16px',
  },
  position: 'absolute',
  top: pxToRem(16),
  right: pxToRem(16),
  paddingX: {
    mobile: '8px',
    mediumDesktop: '17px',
  },
  paddingY: {
    mobile: '2.5px',
    mediumDesktop: '5px',
  },
  borderRadius: '20px',
} as const;

const MOVIES_GRID_SIZES = '(min-width: 1024px) 25vw, (min-width: 500px) 33vw, 50vw';

const MovieCard = forwardRef<Ref, MovieCardProps>(
  (
    {
      title,
      language,
      genres,
      img,
      bookmark,
      rate,
      priority = false,
      sizes = MOVIES_GRID_SIZES,
      sx = {},
      component = 'div',
      componentProps: { disabled, onClick } = {},
      ...props
    },
    ref
  ) => {
    const genresWithDots = genres
      .slice(0, 2)
      .map(genre => [
        <Typography key={genre} component='span'>
          {genre}
        </Typography>,
        <Typography key={genre.concat('Dot')} component='span' sx={genreDotSx}>
          &bull;
        </Typography>,
      ])
      .flat();
    genresWithDots.pop();

    return (
      <Stack
        id={`movie-card`}
        ref={ref}
        component={component}
        sx={[cardSx, ...(Array.isArray(sx) ? sx : [sx])]}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        <ImageBoxFuture
          img={img}
          title={title}
          fallBackImg={MovieFallbackImg}
          // `responsive` with real `sizes`: an intrinsic image builds its srcset
          // from the declared 1600 px (w=1920 / w=3840), whatever the card size.
          // The 1600 × 2400 pair now only sets the 2:3 aspect ratio.
          layout='responsive'
          sizes={sizes}
          objectFit='cover'
          width='1600px'
          height='2400px'
          priority={priority}
          sx={imageSx}
        />
        <Stack sx={infoStackSx}>
          <Typography component='h2' sx={titleSx}>
            {title}
          </Typography>
          <Typography component='span' sx={languageSx}>
            {language}
          </Typography>
          <Stack direction='row' sx={genresRowSx}>
            {genresWithDots}
          </Stack>
        </Stack>
        {bookmark && (
          <Typography
            component='span'
            sx={{
              ...bookmarkBaseSx,
              color: bookmark.fontColor,
              backgroundColor: bookmark.backgroundColor,
            }}
          >
            <BookmarkName name={bookmark.name} />
          </Typography>
        )}
        {rate && (
          <Typography
            component='span'
            sx={{
              ...rateBaseSx,
              color: rate.fontColor,
              backgroundColor: rate.backgroundColor,
            }}
          >
            {rate.title}
          </Typography>
        )}
      </Stack>
    );
  }
);

MovieCard.displayName = 'RawMovieCard';

export default MovieCard;
